import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useRenovationCategoryStore } from './Stores/renovationCategory'
import { Question } from './global'

export function useValidation() {
    const { t } = useI18n()
    const invalidFields = ref<Map<string, string>>(new Map())
    let isWatching = false
    const fails = computed<boolean>(() => !!invalidFields.value.size)

    function validate(questions: Question[]): void {
        invalidFields.value.clear()

        questions.forEach(
            ({
                questionId,
                selectOptions,
                answer,
                required,
                allowMultipleSelect,
                answerType,
                configuration,
            }) => {
                if (!required) {
                    return
                }

                if (answer === undefined || answer === '' || answer.length === 0) {
                    invalidFields.value.set(questionId, t('widget.questions.required'))
                    return
                }

                if (configuration?.rules && answerType === 'numeric') {
                    const { min, max } = configuration.rules

                    if (min && Number(answer) < Number(min)) {
                        invalidFields.value.set(
                            questionId,
                            t('widget.questions.rules.min', { min })
                        )
                        return
                    }

                    if (max && Number(answer) > Number(max)) {
                        invalidFields.value.set(
                            questionId,
                            t('widget.questions.rules.max', { max })
                        )
                        return
                    }
                }

                if (!selectOptions || answerType !== 'select') {
                    return
                }

                const availableOptions = selectOptions.map((selectOption) => selectOption.answerId)

                if (!allowMultipleSelect && !availableOptions.includes(answer as string)) {
                    invalidFields.value.set(questionId, 'widget.questions.required')
                    return
                }

                if (
                    allowMultipleSelect &&
                    (answer as unknown as string[]).some(
                        (answer) => !availableOptions.includes(answer)
                    )
                ) {
                    invalidFields.value.set(questionId, 'widget.questions.required')
                    return
                }
            }
        )

        if (!isWatching) {
            watch(questions, (newQuestions) => validate(newQuestions))
            isWatching = true
        }
    }

    return { invalidFields, fails, validate }
}

export function useEnrollment() {
    const renovationCategoryStore = useRenovationCategoryStore()
    const router = useRouter()

    function start() {
        if (renovationCategoryStore.renovationCategories.length > 1) {
            return router.push({ name: 'renovation-categories.index' })
        }

        if (renovationCategoryStore.renovationCategories.length === 1) {
            renovationCategoryStore.$patch({
                selectedRenovationCategories: [renovationCategoryStore.renovationCategories[0].id],
            })

            if (
                renovationCategoryStore.hasPreQualificationQuestions(
                    renovationCategoryStore.selectedRenovationCategories[0]
                )
            ) {
                return router.push({
                    name: 'renovation-categories.questions',
                    params: { uuid: renovationCategoryStore.selectedRenovationCategories[0] },
                })
            }
        }

        return router.push({ name: 'renovation-categories.techniques.index' })
    }

    return { start }
}
